import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useOutboundEmail } from '@/api/rpc/hooks/communications/useOutboundEmail';
import { EnvelopeSimpleIcon } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { T, Var } from 'gt-react';

const OUTBOUND_EMAIL_SENT_DATE_FORMAT = 'MMM d, yyyy, h:mm a';

interface OutboundEmailPreviewProps {
  entityType: SystemEntity; // Required by preview component registry but not used - type is always OUTBOUND_EMAIL
  entityId: string;
  className?: string;
}

export function OutboundEmailPreview({
  entityId,
  className,
}: OutboundEmailPreviewProps) {
  const { outboundEmail, isLoading, error } = useOutboundEmail(entityId);

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <EnvelopeSimpleIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="outbound remittance email preview title">
              Remittance Email
            </T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="loading state">Loading...</T>
        </p>
      </div>
    );
  }

  if (error || !outboundEmail) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <EnvelopeSimpleIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="outbound remittance email preview title">
              Remittance Email
            </T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="error loading outbound email">
            Unable to load email details
          </T>
        </p>
      </div>
    );
  }

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <EnvelopeSimpleIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold truncate">
          {outboundEmail.subject}
        </h4>
      </div>

      <div className="space-y-2 text-xs w-full">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">
            <T context="email From label">From: </T>
          </span>
          <span className="font-medium truncate block">
            {outboundEmail.from}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">
            <T context="email To label">To: </T>
          </span>
          <span className="font-medium truncate block">{outboundEmail.to}</span>
        </div>

        {outboundEmail.cc && (
          <div>
            <span className="text-muted-foreground">
              <T context="email CC label">CC: </T>
            </span>
            <span className="font-medium truncate block">
              {outboundEmail.cc}
            </span>
          </div>
        )}

        {outboundEmail.sentAt && (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">
              <T context="email sent timestamp label">Sent: </T>
            </span>
            <span className="font-medium text-xs">
              {format(
                new Date(outboundEmail.sentAt),
                OUTBOUND_EMAIL_SENT_DATE_FORMAT,
              )}
            </span>
          </div>
        )}

        {outboundEmail.failureReason && (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">
              <T context="email send error label">Error: </T>
            </span>
            <span className="font-medium text-destructive text-xs">
              <Var>{outboundEmail.failureReason}</Var>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
